import {
  browserTracingIntegration,
  init as sentryInit,
  setUser as sentrySetUser,
} from '@sentry/vue';
import type { App } from 'vue';

import type { CurrentUserDto } from './queries/getCurrentUser';
import router from './router';

const {
  VITE_SENTRY_DSN,
  VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_TRACES_SAMPLE_RATE,
  VITE_SENTRY_RELEASE,
  VITE_API_URL,
} = import.meta.env;

/**
 * Set Sentry user from current user (hashed DB id). Call when current user is loaded (e.g. after auth guard fetch).
 */
export const setSentryUserFromCurrentUser = async (
  user: CurrentUserDto,
): Promise<void> => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(user.id);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    sentrySetUser({ id: hex });
  } catch {
    // Ignore hashing failures in older browsers
  }
};

export const initSentry = (app: App) => {
  if (!VITE_SENTRY_DSN || !VITE_SENTRY_ENVIRONMENT) {
    return;
  }

  const traceOrigin = (() => {
    try {
      return new URL(VITE_API_URL).origin;
    } catch {
      return '';
    }
  })();

  const traceTargets = traceOrigin ? [traceOrigin] : [];

  sentryInit({
    app,
    dsn: VITE_SENTRY_DSN as unknown as string,
    environment: VITE_SENTRY_ENVIRONMENT as unknown as string,
    release: VITE_SENTRY_RELEASE as unknown as string,
    tracesSampleRate: Number(VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0'),
    sendDefaultPii: false,
    tracePropagationTargets: traceTargets,
    integrations: [browserTracingIntegration({ router })],
    ignoreErrors: [
      'ChunkLoadError',
      'Load failed',
      'Network Error',
      'Request aborted',
    ],
  });
};
